import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import PageTitle from "../../layouts/PageTitle";
import {
  useGetAllStaffQuery,
  useGetPmCombinedReportQuery,
  useGetRealEstateTeamsQuery,
  useGetStaffReportsQuery,
} from "../../../store/appApi";
import { selectUser, selectUserRole } from "../../../store/authSlice";
import {
  formatDateLabel,
  formatRoleLabel,
  getFormTypeForRole,
  reportHasOverdueWork,
} from "./reportingConfig";

const chipClass =
  "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold";

const ListStaffReporting = () => {
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "super-admin";
  // A Project Manager oversees their Supervisors / Foremen / Civil Engineers:
  // same filters as a super-admin, scoped by the backend to their own team.
  const isPM = userRole === "Project Manager";
  // Manager Real Estate: sees his team members' reports too (scoped by the backend).
  // Team head (any role) of a Real Estate team — the list returns only the
  // teams this user heads, so a non-empty list means "team head".
  const { data: reTeamData } = useGetRealEstateTeamsQuery({}, { skip: !userRole || isSuperAdmin });
  const isREManager = (reTeamData?.teams || []).length > 0;
  const canFilter = isSuperAdmin || isPM || isREManager;
  const currentUserId = currentUser?.localId || "";
  // Field roles (+ super-admin) can bulk-import Field Execution Journal sheets.
  const canBulkImport = isSuperAdmin || getFormTypeForRole(userRole) === 1;
  const [filters, setFilters] = useState({
    employeeId: "",
    role: "",
    date: null,
    unit: "",
  });
  const [page, setPage] = useState(1);

  // Non-admins can only ever see their own reports — the backend enforces
  // this too, but pin the query here so the filter UI doesn't imply access
  // to everyone else's data.
  const { data: staffData } = useGetAllStaffQuery(
    { page: 1, limit: 500 },
    { skip: !isSuperAdmin },
  );
  const { data: pmData } = useGetPmCombinedReportQuery({}, { skip: !isPM });
  const {
    data: reportData,
    isLoading,
    isFetching,
  } = useGetStaffReportsQuery({
    employeeId: canFilter ? filters.employeeId : currentUserId,
    role: canFilter ? filters.role : "",
    date: filters.date ? filters.date.toISOString() : "",
    unit: filters.unit,
    page,
    limit: 20,
  });

  const reMembers = new Map();
  (reTeamData?.teams || []).forEach((t) =>
    (t.members || []).forEach((m) => reMembers.set(String(m._id), m)),
  );
  if (isREManager && currentUserId)
    reMembers.set(String(currentUserId), {
      _id: currentUserId,
      name: `${currentUser?.name || "Me"} (me)`,
      role: userRole,
    });
  const employees = isREManager
    ? [...reMembers.values()]
    : isPM
    ? (pmData?.members || []).map((m) => ({
        _id: m.employeeId,
        name: m.name,
        role: m.role,
      }))
    : Array.isArray(staffData?.staff)
      ? staffData.staff.filter((item) => item?.status !== false)
      : [];
  const reports = Array.isArray(reportData?.reports) ? reportData.reports : [];
  const units = Array.isArray(reportData?.units) ? reportData.units : [];
  const total = Number(reportData?.total) || 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  const summary = reports.reduce(
    (acc, report) => {
      acc.total += 1;
      acc.completed += report.isComplete ? 1 : 0;
      acc.avg += Number(report.completionPercent) || 0;
      return acc;
    },
    { total: 0, completed: 0, avg: 0 },
  );

  const avgCompletion = reports.length
    ? Math.round(summary.avg / reports.length)
    : 0;

  const statusChip = (report) => {
    if (report.isComplete)
      return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
    if (/review/i.test(String(report.status || "")))
      return "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
    return "bg-sky-50 text-sky-700 ring-1 ring-sky-200";
  };

  return (
    <>
      <PageTitle activeMenu="Staff Reporting" motherMenu="Workforce" />

      <div className="space-y-6">
        <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-xl shadow-slate-200/70">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Reporting index
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-gray-900">
                Staff Reporting Ledger
              </h1>
              <p className="mt-2 max-w-3xl text-sm text-gray-500">
                {isPM
                  ? "Your reports and every report of the Supervisors, Foremen and Civil Engineers under you. Filter by employee, role or date; click a row to open their historical index page and hold / verify their work."
                  : isSuperAdmin
                  ? "Filter by employee, role, or date to inspect the reporting trail. One row per employee — click a row to open their historical index page."
                  : "Your reporting history. Filter by date to narrow it down; click a row to open your historical index page."}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-full bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400"
                  onClick={() => navigate("/staff-reporting/create")}
                >
                  + New Report
                </button>
                {/* PM Combined is for Project Managers (and super-admin, who
                    picks a PM) only — not every user who can filter. */}
                {(isSuperAdmin || isPM) && (
                  <button
                    type="button"
                    className="rounded-full border-2 border-sky-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700 transition hover:bg-sky-50"
                    onClick={() => navigate("/staff-reporting/pm-combined")}
                  >
                    PM Combined Report
                  </button>
                )}
                {canBulkImport && (
                  <button
                    type="button"
                    className="rounded-full border-2 border-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:bg-amber-50"
                    onClick={() => navigate("/staff-reporting/bulk-import")}
                  >
                    Bulk Import
                  </button>
                )}
              </div>
            </div>

            <div className="grid min-w-[320px] grid-cols-3 gap-3 text-center text-xs text-gray-600">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Reports</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">
                  {reports.length}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Completed</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">
                  {summary.completed}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Avg</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">
                  {avgCompletion}%
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-gray-200 bg-white p-5 shadow-xl shadow-slate-200/70">
          <div
            className={`grid gap-4 ${canFilter ? "lg:grid-cols-[1.2fr_1fr_1fr_1fr_auto]" : "lg:grid-cols-[1fr_1fr_auto]"}`}
          >
            {canFilter && (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  Employee
                </label>
                <select
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                  autoComplete="off"
                  value={filters.employeeId}
                  onChange={(event) => {
                    setPage(1);
                    setFilters((prev) => ({
                      ...prev,
                      employeeId: event.target.value,
                    }));
                  }}
                >
                  <option value="">All employees</option>
                  {employees.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.name} - {formatRoleLabel(employee.role)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {canFilter && (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  Role
                </label>
                <select
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                  autoComplete="off"
                  value={filters.role}
                  onChange={(event) => {
                    setPage(1);
                    setFilters((prev) => ({
                      ...prev,
                      role: event.target.value,
                    }));
                  }}
                >
                  <option value="">All roles</option>
                  {[
                    ...new Set(
                      employees
                        .map((employee) => employee.role)
                        .filter(Boolean),
                    ),
                  ].map((role) => (
                    <option key={role} value={role}>
                      {formatRoleLabel(role)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Unit
              </label>
              <select
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                autoComplete="off"
                value={filters.unit}
                onChange={(event) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, unit: event.target.value }));
                }}
              >
                <option value="">All units</option>
                {units.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
                {filters.unit && !units.includes(filters.unit) && (
                  <option value={filters.unit}>{filters.unit}</option>
                )}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Date
              </label>
              <DatePicker
                selected={filters.date}
                onChange={(date) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, date }));
                }}
                placeholderText="Filter by date"
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                autoComplete="off"
                dateFormat="dd/MM/yyyy"
                isClearable
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                type="button"
                className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400"
                onClick={() => {
                  setPage(1);
                  setFilters({
                    employeeId: "",
                    role: "",
                    date: null,
                    unit: "",
                  });
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Report History
              </h2>
              <p className="text-sm text-gray-500">
                One row per employee (their most recently worked sheet).
                Click a row to open their full history.
              </p>
            </div>
            <div className="text-sm text-gray-500">
              Page <span className="text-gray-900">{page}</span> of{" "}
              <span className="text-gray-900">{totalPages}</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-5 py-2.5 text-left font-medium">
                    Employee
                  </th>
                  <th className="px-5 py-2.5 text-left font-medium">Role</th>
                  <th className="px-5 py-2.5 text-left font-medium">
                    Period / Unit
                  </th>
                  <th className="px-5 py-2.5 text-left font-medium">Status</th>
                  <th className="px-5 py-2.5 text-left font-medium">
                    Completion
                  </th>
                  <th className="px-5 py-2.5 text-left font-medium">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {(isLoading || isFetching) && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      Loading reports...
                    </td>
                  </tr>
                )}

                {!isLoading && !isFetching && reports.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      No staff reports were found for the selected filters.
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  !isFetching &&
                  reports.map((report) => {
                    const empId = String(
                      report.employeeId?._id || report.employeeId || "",
                    );
                    const sheetCount = Number(report.sheetCount) || 1;
                    return (
                      <tr
                        key={report._id}
                        className="cursor-pointer transition hover:bg-gray-50"
                        onClick={() =>
                          navigate(`/staff-reporting/employee/${empId}`)
                        }
                      >
                        <td
                          className="px-5 py-2.5 font-semibold text-gray-900"
                          style={{
                            textTransform: "capitalize",
                          }}
                        >
                          {report.employeeId?.name ||
                            report.employeeName ||
                            "—"}
                          {sheetCount > 1 && (
                            <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">
                              {sheetCount} sheets
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-2.5 text-gray-600">
                          {formatRoleLabel(
                            report.employeeId?.role || report.employeeRole,
                          )}
                        </td>
                        <td className="px-5 py-2.5 text-gray-600">
                          {Number(report.formType) === 1 ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                Unit
                              </span>
                              {report.periodLabel || "—"}
                            </span>
                          ) : (
                            report.periodLabel ||
                            formatDateLabel(report.reportDate)
                          )}
                        </td>
                        <td className="px-5 py-2.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span
                              className={`${chipClass} ${statusChip(report)}`}
                            >
                              {report.noReport
                                ? "No report updated yet"
                                : report.status ||
                                  (report.isComplete ? "Complete" : "Pending")}
                            </span>
                            {!report.isComplete &&
                              reportHasOverdueWork(report.formData) && (
                                <span
                                  className={`${chipClass} bg-red-50 text-red-700 ring-1 ring-red-200`}
                                >
                                  Overdue
                                </span>
                              )}
                          </div>
                        </td>
                        <td className="px-5 py-2.5 text-gray-600">
                          <div className="flex items-center gap-3">
                            <div className="h-2 flex-1 rounded-full bg-gray-200">
                              <div
                                className="h-2 rounded-full bg-amber-500"
                                style={{
                                  width: `${Math.min(100, Number(report.completionPercent) || 0)}%`,
                                }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">
                              {Number(report.completionPercent) || 0}%
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-2.5 text-gray-600">
                          {report.noReport
                            ? "—"
                            : formatDateLabel(
                                report.lastEditedAt || report.updatedAt,
                              )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 text-sm text-gray-500">
            <div>
              Showing {reports.length} of {total} report{total === 1 ? "" : "s"}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-xl border-2 border-amber-500 px-3 py-2 text-xs font-semibold text-gray-700 disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Prev
              </button>
              <button
                type="button"
                className="rounded-xl border-2 border-amber-500 px-3 py-2 text-xs font-semibold text-gray-700 disabled:opacity-50"
                disabled={page >= totalPages}
                onClick={() =>
                  setPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Next
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ListStaffReporting;
